import React, { useState, useRef } from 'react'
import { Modal, Button, Icon, message } from 'antd'
import useVideoUpload from './useVideoUpload'

const ACCEPTED_EXTENSIONS = ['.mp4', '.mov', '.avi', '.webm', '.mkv']
const MAX_FILE_SIZE_MB = 500

const formatFileSize = bytes => {
  if (!bytes) return ''
  const mb = bytes / (1024 * 1024)
  if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`
  return `${mb.toFixed(1)} MB`
}

const formatFileName = name => {
  if (!name) return 'video'
  if (name.length <= 32) return name
  const ext = name.split('.').pop()
  const base = name.slice(0, name.length - ext.length - 1)
  return `${base.slice(0, 18)}...${base.slice(-6)}.${ext}`
}

const CircularProgress = ({ percent, size = 80, strokeWidth = 5 }) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percent / 100) * circumference

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: 'rotate(-90deg)' }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#f0f0f0"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#1890ff"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.3s ease' }}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          transform: 'rotate(90deg)',
          transformOrigin: 'center',
          fontSize: 16,
          fontWeight: 600,
          fill: '#262626'
        }}
      >
        {percent}%
      </text>
    </svg>
  )
}

const VideoUploadModal = ({ visible, onCancel, onUploadComplete, videoId }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadState, setUploadState] = useState('idle')
  const fileInputRef = useRef(null)

  const { isUploading, progress, uploadVideo } = useVideoUpload({ videoId })

  const handleFileSelect = e => {
    const file = e.target.files[0]
    if (!file) return

    setSelectedFile(file)
    setUploadState('uploading')

    uploadVideo(file).then(result => {
      if (result.success && result.url) {
        setUploadState('done')
        onUploadComplete(result.url)
      } else {
        setUploadState('error')
        message.error('Upload failed. Please try again.')
      }
    })

    e.target.value = ''
  }

  const handleOpenFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleClose = () => {
    if (isUploading) return
    setSelectedFile(null)
    setUploadState('idle')
    onCancel()
  }

  const handleReset = () => {
    setSelectedFile(null)
    setUploadState('idle')
  }

  return (
    <Modal
      title="Upload Video"
      visible={visible}
      onCancel={handleClose}
      closable={!isUploading}
      maskClosable={!isUploading}
      footer={
        uploadState === 'done' ? (
          <Button type="primary" onClick={handleClose}>
            Done
          </Button>
        ) : uploadState === 'error' ? (
          <Button type="primary" onClick={handleReset}>
            Try Again
          </Button>
        ) : null
      }
      centered
      width={460}
      destroyOnClose
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={ACCEPTED_EXTENSIONS.join(',')}
        style={{ display: 'none' }}
      />

      {uploadState === 'idle' && (
        <>
          <div
            onClick={handleOpenFilePicker}
            style={{
              border: '2px dashed #d9d9d9',
              borderRadius: 10,
              padding: '36px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              background: '#ffffff',
              transition: 'border-color 0.3s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#1890ff'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#d9d9d9'
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: '#e6f7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px'
              }}
            >
              <Icon
                type="cloud-upload"
                style={{ fontSize: 24, color: '#1890ff' }}
              />
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: '#262626',
                marginBottom: 4
              }}
            >
              Click to browse
            </div>
            <div style={{ fontSize: 12, color: '#595959' }}>
              Select a video from your computer
            </div>
          </div>
          <div
            style={{
              marginTop: 14,
              padding: '10px 14px',
              background: '#fafafa',
              borderRadius: 6,
              fontSize: 12,
              color: '#595959',
              lineHeight: 1.7
            }}
          >
            <div style={{ marginBottom: 4, fontWeight: 500, color: '#262626' }}>
              Requirements
            </div>
            <div>
              <span style={{ color: '#8c8c8c' }}>Formats:</span>{' '}
              {ACCEPTED_EXTENSIONS.map(ext => ext.toUpperCase().slice(1)).join(
                ', '
              )}
            </div>
            <div>
              <span style={{ color: '#8c8c8c' }}>Max size:</span>{' '}
              {MAX_FILE_SIZE_MB} MB
            </div>
          </div>
        </>
      )}

      {uploadState === 'uploading' && (
        <div style={{ padding: '20px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <CircularProgress percent={progress} size={88} strokeWidth={5} />
          </div>
          <div
            style={{
              background: '#fafafa',
              borderRadius: 8,
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}
          >
            <Icon type="file" style={{ fontSize: 20, color: '#595959' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#262626',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {formatFileName(selectedFile?.name)}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                {formatFileSize(selectedFile?.size)}
              </div>
            </div>
          </div>
        </div>
      )}

      {uploadState === 'done' && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#f6ffed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}
          >
            <Icon
              type="check"
              style={{ fontSize: 32, color: '#52c41a', fontWeight: 600 }}
            />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>
            Video uploaded successfully
          </div>
        </div>
      )}

      {uploadState === 'error' && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#fff2f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}
          >
            <Icon
              type="close"
              style={{ fontSize: 32, color: '#ff4d4f', fontWeight: 600 }}
            />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>
            Upload failed
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            Check your connection and try again.
          </div>
        </div>
      )}
    </Modal>
  )
}

export default VideoUploadModal
